import { useMemo, useState } from "react";
import {
  useGetOrphanAssetsQuery,
  useDeleteOrphanAssetsMutation,
  useAdoptOrphanAssetMutation,
} from "../../redux/api";
import type { OrphanAsset, OrphanReason } from "../../redux/api";
import {
  IconTrash,
  IconImage,
  IconEye,
  IconPlus,
  IconAlert,
  IconShield,
  IconBox,
} from "../../components/icons";
import { StatCard } from "../../components/StatCard";
import { ImagePreviewModal } from "../../components/ImagePreviewModal";
import { RowMenu } from "../../components/RowMenu";

const formatSize = (bytes: number) =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1024 * 1024
      ? `${Math.round(bytes / 1024)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : "—");

const isImage = (fileName: string) => /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(fileName);
const isVideo = (fileName: string) => /\.(mp4|mov|webm|m3u8)$/i.test(fileName);

// Which pill colour each reason gets. Only the protected case is a warning —
// everything else is ordinary reclaimable litter, and colouring it all red
// would make the one row that must NOT be deleted blend in.
const REASON_TONE: Record<OrphanReason, string> = {
  HLS_SEGMENT: "pill--warning",
  CONVERTED_SOURCE: "pill--neutral",
  EDIT_SOURCE: "pill--neutral",
  SUPERSEDED_UPLOAD: "pill--neutral",
  ABANDONED_UPLOAD: "pill--neutral",
};

/**
 * Platform Owner → Storage Cleanup.
 *
 * Lists every object in this environment's S3 prefix that no record points at,
 * says why each one is there, and lets a platform owner reclaim the space.
 *
 * The "why" matters more than it looks. Nothing in this product ever deleted an
 * S3 object before this screen existed: uploads write to a fresh UUID key,
 * conversion and editing write new objects and deliberately keep their source,
 * and the Gallery's delete is a soft delete that leaves the file alone. So
 * orphans are not a bug to be fixed once — they are the steady-state output of
 * normal use, and this screen is the thing that clears them.
 *
 * Deleting is permanent (the bucket has no versioning), so the server re-derives
 * the orphan set at the moment of deletion rather than trusting the selection
 * posted from here.
 */
const OrphanAssets = () => {
  const { data, isLoading, isFetching, error, refetch } = useGetOrphanAssetsQuery();
  const [deleteOrphans, { isLoading: deleting }] = useDeleteOrphanAssetsMutation();
  const [adoptOrphan, { isLoading: adopting }] = useAdoptOrphanAssetMutation();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reasonFilter, setReasonFilter] = useState<string>("");
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [preview, setPreview] = useState<OrphanAsset | null>(null);
  const [showExplainer, setShowExplainer] = useState(false);

  const summary = data?.summary;
  const orphans = useMemo(() => data?.orphans ?? [], [data]);

  const busy = deleting || adopting;

  const reasons = useMemo(() => {
    const seen = new Map<string, string>();
    for (const orphan of orphans) seen.set(orphan.reason, orphan.reasonLabel);
    return [...seen.entries()];
  }, [orphans]);

  const organizations = useMemo(() => {
    const seen = new Map<string, string>();
    for (const orphan of orphans) {
      if (orphan.organizationId) {
        seen.set(orphan.organizationId, orphan.organizationName ?? orphan.organizationId);
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [orphans]);

  const visible = useMemo(
    () =>
      orphans.filter(
        (orphan) =>
          (!reasonFilter || orphan.reason === reasonFilter) &&
          (!orgFilter || orphan.organizationId === orgFilter),
      ),
    [orphans, reasonFilter, orgFilter],
  );

  // Only ever counts rows the server would actually accept, so the button's
  // number matches what the confirm dialog promises.
  const selectableVisible = useMemo(() => visible.filter((o) => o.deletable), [visible]);
  const selectedBytes = useMemo(
    () =>
      orphans
        .filter((orphan) => selected.has(orphan.key))
        .reduce((sum, orphan) => sum + orphan.size, 0),
    [orphans, selected],
  );

  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((o) => selected.has(o.key));

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) selectableVisible.forEach((o) => next.delete(o.key));
      else selectableVisible.forEach((o) => next.add(o.key));
      return next;
    });

  const handleDeleteSelected = async () => {
    const keys = [...selected];
    if (keys.length === 0) return;
    if (
      !window.confirm(
        `Permanently delete ${keys.length} file(s), freeing ${formatSize(selectedBytes)}?\n\n` +
          `This removes them from S3 for good — the bucket is not versioned, so there is no undo.`,
      )
    ) {
      return;
    }
    try {
      const result = await deleteOrphans(keys).unwrap();
      setSelected(new Set());
      if (result.errors.length) {
        window.alert(
          `Deleted ${result.deletedCount} file(s), freeing ${formatSize(result.deletedBytes)}.\n\n` +
            `${result.errors.length} were skipped:\n` +
            result.errors
              .slice(0, 10)
              .map((e) => `• ${e.key.split("/").pop()} — ${e.reason}`)
              .join("\n"),
        );
      } else {
        window.alert(
          `Deleted ${result.deletedCount} file(s), freeing ${formatSize(result.deletedBytes)}.`,
        );
      }
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to delete files.");
    }
  };

  const handleDeleteOne = async (orphan: OrphanAsset) => {
    if (!window.confirm(`Permanently delete "${orphan.fileName}"? This cannot be undone.`)) return;
    try {
      const result = await deleteOrphans([orphan.key]).unwrap();
      if (result.errors.length) window.alert(result.errors[0].reason);
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to delete file.");
    }
  };

  const handleAdopt = async (orphan: OrphanAsset) => {
    try {
      await adoptOrphan(orphan.key).unwrap();
      window.alert(
        `"${orphan.fileName}" was added to ${orphan.organizationName ?? "the organization"}'s Gallery. ` +
          `It can now be attached to a record again.`,
      );
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to add to gallery.");
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to scan storage"
    : null;

  return (
    <div className="dashboard">
      <div className="stat-grid">
        <StatCard
          label="Objects in bucket"
          value={isLoading ? "…" : (summary?.totalObjects ?? 0)}
          hint={summary ? `under ${summary.bucketPrefix} · ${formatSize(summary.totalBytes)}` : undefined}
          tone="primary"
          icon={<IconBox />}
        />
        <StatCard
          label="In use"
          value={isLoading ? "…" : (summary?.referencedObjects ?? 0)}
          hint="referenced by a record"
          tone="success"
          icon={<IconImage />}
        />
        <StatCard
          label="Orphaned"
          value={isLoading ? "…" : (summary?.orphanCount ?? 0)}
          hint={summary ? formatSize(summary.orphanBytes) : undefined}
          tone="warning"
          icon={<IconAlert />}
        />
        <StatCard
          label="Reclaimable"
          value={isLoading ? "…" : formatSize(summary?.deletableBytes ?? 0)}
          hint={
            summary?.protectedCount
              ? `${summary.deletableCount} files · ${summary.protectedCount} protected`
              : `${summary?.deletableCount ?? 0} files`
          }
          tone="danger"
          icon={<IconTrash />}
        />
      </div>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Storage Cleanup</h2>
            <p className="panel__sub">
              {isLoading
                ? "Scanning…"
                : `Showing ${visible.length} of ${orphans.length} orphaned file(s)` +
                  (selected.size ? ` · ${selected.size} selected (${formatSize(selectedBytes)})` : "")}
            </p>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setShowExplainer((v) => !v)}
            >
              {showExplainer ? "Hide" : "Why do these exist?"}
            </button>
            {reasons.length > 1 && (
              <select
                className="select"
                value={reasonFilter}
                onChange={(e) => setReasonFilter(e.target.value)}
              >
                <option value="">All reasons</option>
                {reasons.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            )}
            {organizations.length > 1 && (
              <select
                className="select"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
              >
                <option value="">All organizations</option>
                {organizations.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching || busy}
            >
              {isFetching ? "Rescanning…" : "Rescan"}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={handleDeleteSelected}
              disabled={busy || selected.size === 0}
            >
              {deleting ? "Deleting…" : `Delete selected${selected.size ? ` (${selected.size})` : ""}`}
            </button>
          </div>
        </div>

        {showExplainer && (
          <div className="panel__note">
            <p>
              <strong>Nothing in this product has ever deleted an S3 object.</strong> Uploads go to a
              new UUID key, image/video conversion and editing write a <em>new</em> file and keep the
              original on purpose, and deleting from the Gallery only hides the record — the file
              stays. So a file becomes orphaned the moment whatever pointed at it is repointed or
              removed, and then stays in the bucket forever. That is what this screen clears.
            </p>
            <ul>
              <li>
                <strong>Replaced by a newer upload</strong> — the same file was uploaded again and the
                record now points at the new copy. Re-running a seeding script, or a user swapping a
                product photo, both land here.
              </li>
              <li>
                <strong>Pre-conversion original</strong> — a HEIC or video upload. The converted PNG
                or HLS stream is what the record uses; this is the source it was made from.
              </li>
              <li>
                <strong>Pre-edit original</strong> — an edited copy was made from this file and is the
                one in use. Editing never overwrites its source.
              </li>
              <li>
                <strong>Abandoned upload</strong> — the file reached S3 but was never attached to
                anything. Uploading is a presigned PUT followed by a save; only the first half
                happened.
              </li>
              <li>
                <strong>Video stream segment</strong> — <em>protected.</em> A converted video is a
                playlist plus segments, and only the playlist is ever stored on a record. The
                segments always look orphaned and are always required, so they cannot be selected.
              </li>
            </ul>
          </div>
        )}

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    disabled={selectableVisible.length === 0 || busy}
                  />
                </th>
                <th style={{ width: 56 }}>File</th>
                <th>Name</th>
                <th>Organization</th>
                <th>Reason</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={8}>
                    Scanning the bucket…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={8}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={8}>
                    {orphans.length === 0
                      ? "No orphaned files — every object in this environment is referenced by a record."
                      : "No files match the current filters."}
                  </td>
                </tr>
              ) : (
                visible.map((orphan) => (
                  <tr key={orphan.key}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${orphan.fileName}`}
                        checked={selected.has(orphan.key)}
                        onChange={() => toggle(orphan.key)}
                        disabled={!orphan.deletable || busy}
                        title={orphan.deletable ? undefined : orphan.reasonDetail}
                      />
                    </td>
                    <td>
                      {orphan.url && isImage(orphan.fileName) ? (
                        <img
                          src={orphan.url}
                          alt={orphan.fileName}
                          onClick={() => setPreview(orphan)}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: "var(--radius-sm)",
                            cursor: "pointer",
                            display: "block",
                          }}
                        />
                      ) : (
                        <span className="pill pill--neutral">
                          {orphan.fileName.split(".").pop()?.toUpperCase() ?? "FILE"}
                        </span>
                      )}
                    </td>
                    <td className="cell-title">
                      {orphan.fileName}
                      {orphan.entityType && (
                        <div className="stat-card__hint">
                          {orphan.entityType}
                          {orphan.entityId ? ` · ${orphan.entityId}` : ""}
                        </div>
                      )}
                    </td>
                    <td>{orphan.organizationName ?? orphan.organizationId ?? "—"}</td>
                    <td>
                      <span
                        className={`pill ${REASON_TONE[orphan.reason] ?? "pill--neutral"}`}
                        title={orphan.reasonDetail}
                      >
                        {!orphan.deletable && <IconShield size={12} />} {orphan.reasonLabel}
                      </span>
                    </td>
                    <td>{formatSize(orphan.size)}</td>
                    <td className="cell-date">{formatDate(orphan.lastModified)}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Preview",
                            icon: <IconEye size={16} />,
                            onSelect: () => setPreview(orphan),
                            hidden: !orphan.url || (!isImage(orphan.fileName) && !isVideo(orphan.fileName)),
                          },
                          {
                            label: "Add to Gallery",
                            icon: <IconPlus size={16} />,
                            onSelect: () => handleAdopt(orphan),
                            disabled: busy || !orphan.organizationId,
                          },
                          {
                            label: "Delete forever",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDeleteOne(orphan),
                            danger: true,
                            disabled: busy || !orphan.deletable,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ImagePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url}
        fileName={preview?.fileName}
        isVideo={preview ? isVideo(preview.fileName) : false}
      />
    </div>
  );
};

export default OrphanAssets;
